import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { organizationApi } from 'src/api/organization';
import { useSnackbar } from 'src/components/snackbar';
import { GetOrganizationDto } from 'woofwoof-api';
import { translateKey } from 'src/locales/translate';


interface OrganizationDetailsFormStatusModalProps {
  fetchOrganization: () => void;
  organization: GetOrganizationDto;
  isUpdateLoading: boolean;
}

export default function OrganizationDetailsFormStatusModal({
  fetchOrganization,
  organization,
  isUpdateLoading,
}: OrganizationDetailsFormStatusModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const { organizationId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { mutateAsync: updateOrganization } = useMutation({
    mutationFn: (id: string) =>
      organizationApi.organizationControllerUpdateOrganization({
        updateOrganizationDto: {
          isActive: !organization.isActive,
        },
        id,
      }),
  });

  const closeModal = () => setIsOpen(false);

  const openModal = () => setIsOpen(true);

  const handleStatusChange = async () => {
    setIsStatusLoading(true);
    try {
      await updateOrganization(organizationId!);
      closeModal();
      enqueueSnackbar(translateKey("hardcoded.status_zostal_zmieniony"), {
        variant: 'success',
      });
      fetchOrganization();
    } catch (error) {
      enqueueSnackbar(error.message || error, {
        variant: 'error',
      });
    } finally {
      setIsStatusLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={openModal}
        disabled={isUpdateLoading || isStatusLoading}
      >
        {organization.isActive ? 'Deaktywuj' : 'Aktywuj'}
      </Button>
      <Dialog open={isOpen} onClose={closeModal}>
        <DialogTitle>
          {organization.isActive
            ? translateKey("hardcoded.probujesz_deaktywowac_organizacje")
            : translateKey("hardcoded.probujesz_aktywowac_organizacje")}
        </DialogTitle>

        <DialogContent sx={{ maxWidth: '400px' }}>
          <DialogContentText id="alert-dialog-description">
            Czy na pewno chcesz{' '}
            {organization.isActive ? translateKey("hardcoded.deaktywowac") : translateKey("hardcoded.aktywowac")}{translateKey("hardcoded.organizacje_ta_akcja_spowoduje")}{organization.isActive ? 'ukrycie' : 'pokazanie'}{' '}{translateKey("hardcoded.organizacji_w_wyszukiwarce_przez_co_bedzie")}{' '}
            {organization.isActive ? 'niewidoczna' : 'widoczna'}{translateKey("hardcoded.dla_uzytkownikow_aplikacji")}</DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={closeModal}>{translateKey("common.cancel")}</Button>
          <LoadingButton
            variant="contained"
            onClick={handleStatusChange}
            loading={isStatusLoading}
          >
            <span>{organization.isActive ? 'Deaktywuj' : 'Aktywuj'}</span>
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
